import { useMemo } from 'react';
import { selectCurrUser } from '@/store/bootstrap/selectors';
// eslint-disable-next-line no-restricted-syntax -- the one sanctioned raw useFlags import; everything else goes through useKFlags
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSafeSelector } from './use-safe-selector';

// Developers can override flags here and test them in development
const devOverrideFlags = {
  // BE flag: t_internal_email_activity — gates the internal email activity, its
  // template category, and the send + automation surfaces.
  tInternalEmailActivity: true,
  feAiSearch: false,
  feJobProfile: true,
  tNLevelTransfer: true,
  feNewVideoPlayer: true,
  // agents
  feJobTeammates: true,
  feCoordinatorAgent: false,
  feFraudDetection: true,
  feFraudMax: true,
  feCrBuyFlow: false,
  feAiAutoRejectEmail: true,
  feAgentForce: false,
  feSchedulingRevampV2: true,
  feRemoteInterview: true,
  feInterviewBuffer: true,
  feManualInterviewBuffer: false,
  feScheduleAllV2: true,
  tUnifiedCreditManagement: true,
  tConsentManagement: true,
  tCustomiseWorkTypes: true,
  tDocusignOnAllStages: true,
  feKanbanPragmaticDnd: true,
  feStageAutomation: true,
  feOfferTemplateMultiDocuments: true,
  feOfferTemplatePdfPreview: true,
  feDarwinbox: true,
  // Gates the settings-drawer employee-form builder (step 2). Off, "Edit"
  // falls back to the original single-page connect-only drawer.
  feDarwinboxFormBuilder: true,
  feSpotdraft: true,
  feOfferFieldReorder: true,
  // Gates the job/candidate field REORDER + status work only. Off, both
  // settings pages fall back to the custom-fields-only page that shipped
  // before it, and the consuming forms ignore the stored order.
  feJobCandidateFieldReorder: true,
  flowsBulkActions: true,
  feJobListingChanges: true,
  tInterviewerPrepAgent: true,
  internalJobsPost: true,
  feDocusignOfferPreview: true,
  feSpringverify: true,
  tOfferSections: true,
  tOfferRichText: true,
  feConversationalAnalytics: false,
  pInterviewerAssistant: false,
  tDemoInterviewHarness: false,
  // Ask Kula voice mode (BE flag: t_voice_agent). On in dev to exercise the
  // composer mic + LiveKit voice overlay; prod stays off until LaunchDarkly.
  tVoiceAgent: true,
  // Google Meet external host (BE flag: t_google_meet_external_host). Enables the
  // host picker for Google Meet interviews (active_hosts?type=gmeet).
  tGoogleMeetExternalHost: true,
  careersUiRevamp: true,
  feDepartmentHeadRole: true,
  fePat: true,
  // FE flag: Clone activity on interview plan rows and empty stages.
  feCloneActivity: true,
  tCloseJobBulkReject: true,
};

// Module scope so the no-current-user path reuses one object identity. Built in
// the hook body it was a fresh literal every render, which would defeat the
// memo below for every logged-out / non-redux (assessment, signup) render.
const DEFAULT_FEATURE_OPTIONS = {
  ats_enabled: false,
  circles_enabled: false,
  outreach_enabled: false,
};

export const useKFlags = () => {
  const flags = useFlags();

  // Wrap the selector in useSafeSelector to prevent errors for non redux app like assessment or signup and assign default value for currUser
  const currUser = useSafeSelector(selectCurrUser);
  const featureOptions = currUser
    ? currUser.feature_options
    : DEFAULT_FEATURE_OPTIONS;

  const isDevEnv = import.meta.env.VITE_ENV === 'development';

  // This hook is called in ~400 files, so it runs many times per render pass.
  // Un-memoised it spread the whole LaunchDarkly flag set into a brand-new
  // object on every one of those calls: wasted allocation, and a fresh identity
  // that invalidates any consumer dependency array holding the returned object.
  // `flags` (LD context value) and `currUser.feature_options` (redux slice) are
  // both stable between actual changes, so the merged result can be too.
  return useMemo(
    () => ({
      ...flags,
      ...featureOptions,
      ...(isDevEnv ? devOverrideFlags : {}),
    }),
    [flags, featureOptions, isDevEnv]
  );
};
